/* eslint-disable no-nested-ternary */
import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Spacings } from '@styles/index';
import H6 from '@base/H6/H6';
import P2 from '@base/P2/P2';
import Paper from '@base/Paper/Paper';
import Play from '@icon/Syllabus/Play';
import VerticalDots from '@icon/Planner/VerticalDots';
import { useRouter } from 'next/router';
import { IChapterInfo, ILesson } from 'types/syllabus';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import Image from '@base/Image';
import Dialog from '@comp/Dialog/Dialog';
import { useSelector } from 'react-redux';
import { getIsLoggedIn } from '@selectors/auth';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { courseStates } from '@constants/index';
import LessonType from '@cont/Syllabus/constants';
import { getIsSubscribedToGoal } from '@selectors/subscription';
import pushRoute from '@utils/pushRoute';
import { segment } from '@actions/analytics';
import {
  CHECKOUT_STARTED,
  SYLLABUS_LOCKED_ITEM_CLICK,
  SYLLABUS_PURCHASE_DIALOG_MAY_BE_LATER,
  SYLLABUS_PURCHASE_DIALOG_TAP_OUT
} from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import getDurationStr from '@utils/getDurationStr';
import { getFullName } from '@models/User';
import { getIsRCPlayAllowed } from '@api/selectors/constants';
import useConstants from '@api/hooks/useConstants';
import FetchAndRender from './FetchAndRender';
import RecordedVideoBlockDialog from './RecordedVideoBlockDialog';

export interface ISyllabusLessonProps {
  lessonInfo: ILesson;
  conceptInfo: IChapterInfo;
  color: string;
  lightColor: string;
  onClick: (event: any) => void;
  disableTopicPage: (value: boolean) => void;
  isSyllabusPageEducatorSpecific: boolean;
  isRecordedGoal: boolean;
}
interface IIconWrapper {
  $bgColor: string;
}

const Wrapper = styled(Paper)`
  display: flex;
  justify-content: space-between;
  padding: ${Spacings.SPACING_6B};
  cursor: pointer;
  &:hover {
    box-shadow: ${boxShadowGenerator({
      xOffset: '0px',
      yOffset: Spacings.SPACING_1B,
      blur: Spacings.SPACING_2B,
      spread: '0px'
    })};
  }
`;

const IconWrapper = styled.div<IIconWrapper>`
  background-color: ${({ $bgColor }) => $bgColor};
  border-radius: 100%;
  height: ${Spacings.SPACING_10B};
  width: ${Spacings.SPACING_10B};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InfoWrapper = styled.div`
  display: grid;
  grid-template-columns: 0fr 4fr;
  column-gap: ${Spacings.SPACING_4B};
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${Spacings.SPACING_2};
`;

const StyP2 = styled(P2)`
  line-height: 18px;
`;

const AuthorTitle = styled(P2)`
  margin-top: ${Spacings.SPACING_1};
`;

const MenuButton = styled.div`
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${Spacings.SPACING_6B};
  width: ${Spacings.SPACING_6B};
  cursor: pointer;
  transition: background-color 200ms ease-in-out;
  position: relative;
  margin-left: ${Spacings.SPACING_4B};
  &:hover {
    background-color: var(--color-divider);
  }
`;

const Lesson: React.FC<ISyllabusLessonProps> = ({
  lessonInfo,
  conceptInfo,
  color,
  lightColor,
  onClick,
  disableTopicPage,
  isSyllabusPageEducatorSpecific,
  isRecordedGoal
}) => {
  const {
    title: chapterTitle,
    isCompleted,
    videoDuration,
    uid,
    state,
    tags,
    author
  } = lessonInfo;
  const {
    query: { goalUID, goalSlug }
  } = useRouter();
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isSubscribed = useSelector(getIsSubscribedToGoal(goalUID));
  const [openCLXInterface, setOpenCLXInterface] = useState(false);
  const [openTooltip, setOpenTooltip] = useState(false);
  const [showGetSubDialog, setShowGetSubDialog] = useState(false);
  const [showRecordedGoalDialog, setShowRecordedGoalDialog] = useState(false);
  const isRecordedContentPlayAllowed = useConstants(
    getIsRCPlayAllowed(goalUID)
  );

  const isLocked = isLoggedIn
    ? isSubscribed
      ? false
      : !tags.find((tag) => tag === LessonType.FREE_PLUS_LESSON)
    : true;
  const isComingSoon = state === courseStates.CREATED;
  let goalSegmentData = useGoalEventProps({ goalUID: String(goalUID) });
  goalSegmentData = {
    ...goalSegmentData,
    topology_id: conceptInfo.uid,
    topology_name: conceptInfo.title,
    topology_level: conceptInfo.level,
    lesson_name: lessonInfo.title,
    lesson_uid: lessonInfo.uid,
    last_primary_source: 'Syllabus Topic Page'
  };

  const authorName = useMemo(() => {
    return getFullName(author?.firstName, author?.lastName);
  }, [author]);

  const onMouseEnter = () => {
    if (isLocked && isLoggedIn) setOpenTooltip(true);
  };

  const onMouseLeave = () => {
    setOpenTooltip(false);
  };

  const onLessonClick = async () => {
    if (isRecordedGoal && !isRecordedContentPlayAllowed) {
      return setShowRecordedGoalDialog(true);
    }
    if (isLocked) {
      if (!isLoggedIn) {
        openLoginDrawer();
      } else {
        segment.track(SYLLABUS_LOCKED_ITEM_CLICK, {
          ...goalSegmentData,
          last_primary_source_section: 'Locked Lesson Click'
        });
        setShowGetSubDialog(true);
      }
    } else if (!isComingSoon) {
      disableTopicPage(true);
      setOpenCLXInterface(true);
    }
  };

  const exitGetSubDialog = (fromMaybeLater) => {
    setShowGetSubDialog(false);
    const eventName = fromMaybeLater
      ? SYLLABUS_PURCHASE_DIALOG_MAY_BE_LATER
      : SYLLABUS_PURCHASE_DIALOG_TAP_OUT;
    segment.track(eventName, {
      ...goalSegmentData,
      last_primary_source_section: fromMaybeLater ? 'Maybe later' : 'Tap out'
    });
  };

  const handleRecordedDialogClose = () => {
    setShowRecordedGoalDialog(false);
  };

  const gotoCheckout = () => {
    segment.track(CHECKOUT_STARTED, {
      ...goalSegmentData,
      last_primary_source_section: 'Get subscription popup'
    });
    pushRoute(
      '/goal/[goalSlug]/[goalUID]/subscribe',
      `/goal/${goalSlug}/${goalUID}/subscribe`
    );
  };

  return (
    <>
      <Wrapper
        onMouseOver={onMouseEnter}
        onMouseOut={onMouseLeave}
        onClick={onLessonClick}
      >
        <InfoWrapper>
          <IconWrapper
            // @ts-ignore
            $bgColor={
              isLocked
                ? 'var(--color-divider)'
                : isCompleted
                ? lightColor
                : isComingSoon
                ? 'var(--color-base-1)'
                : color
            }
          >
            {isComingSoon ? (
              <Image
                width={40}
                height={40}
                src="syllabus/coming-soon.svg"
                alt="soon"
              />
            ) : (
              <Play
                size="13px"
                color={
                  isLocked
                    ? 'var(--color-text-secondary)'
                    : isCompleted
                    ? color
                    : 'var(--color-i-white)'
                }
              />
            )}
          </IconWrapper>
          <div>
            <H6>{chapterTitle}</H6>
            <MetaInfo>
              <StyP2 color="var(--color-text-secondary)">
                {isComingSoon ? (
                  'Coming soon'
                ) : (
                  <>
                    {getDurationStr(videoDuration)}
                    {videoDuration && isCompleted && <> &#8226; </>}
                    {isCompleted && 'Completed'}
                  </>
                )}
              </StyP2>
            </MetaInfo>
            {isRecordedGoal && (
              <AuthorTitle color="var(--color-text-secondary)">
                {authorName}
              </AuthorTitle>
            )}
          </div>
        </InfoWrapper>
        {isLocked ? (
          <Image
            width={24}
            height={24}
            src="syllabus/locked-video.svg"
            alt="locked"
          />
        ) : (
          !isComingSoon &&
          isSubscribed && (
            <MenuButton onClick={onClick}>
              <VerticalDots
                size={Spacings.SPACING_6B}
                color="var(--color-text-secondary)"
              />
            </MenuButton>
          )
        )}
      </Wrapper>

      {openCLXInterface && (
        <FetchAndRender
          uid={uid}
          goalUID={goalUID as string}
          closeCLXInterface={setOpenCLXInterface}
          disableTopicPage={disableTopicPage}
          isDemoVideo={!isLocked}
          isSyllabusPageEducatorSpecific={isSyllabusPageEducatorSpecific}
          source="syllabus_feed"
        />
      )}
      <Dialog
        type="default"
        dialogState={showGetSubDialog}
        onClose={exitGetSubDialog}
        acceptLabel="Get subscription"
        acceptLabelVariant="filled"
        acceptLabelTheme="green"
        rejectLabel="Maybe later"
        rejectLabelVariant="hollow"
        acceptOnClick={gotoCheckout}
        rejectOnClick={exitGetSubDialog}
        heading="Access high quality videos with subscription"
        message="Get subscription to watch high-quality structured videos on all topics"
        messageColor="var(--color-i-gray)"
      />
      <RecordedVideoBlockDialog
        isOpen={showRecordedGoalDialog}
        onClose={handleRecordedDialogClose}
      />
    </>
  );
};

export default Lesson;
